import {
  Box,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
  Tabs,
  Tab,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { friendlyDateTime } from "Helpers/dates";
import { useCallback, useState, useEffect, useMemo } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Button } from "@mui/material";
import ValidatorAutocomplete from "features/forms/ValidatorAutocomplete";
import { useLanderTemplates } from "../hooks/useLanderTemplates";
import {
  ApiAIPromptStatus,
  ApiComponentType,
  ApiSimpleComponent,
  ApiMediaType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { useSelectedProject } from "Hooks";
import { generateComponent } from "features/campaigns/services/componentService";
import { usePollPendingComponents } from "features/campaigns";
import AdSelector, { AdOption } from "features/rezonate/components/AdSelector";
import { useSubdomains } from "Hooks/useSubdomains";
import { buildPreviewUrl } from "features/lightningLanders/utils";
import {
  getLatestLiveSubdomain,
  getSelectableSubdomains,
} from "features/subdomains/utils";
import { SubdomainSelector } from "features/subdomains";
import UploaderButton from "features/media/components/UploaderButton";

interface Template {
  id: number;
  name: string;
}

interface NewLanderDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    name: string,
    templateId?: number,
    component?: ApiSimpleComponent,
    adId?: number,
    createSmartLink?: boolean,
    subdomainId?: number,
  ) => void;
  onComponentCreated?: (
    component: ApiSimpleComponent,
    name?: string,
    adId?: number,
    createSmartLink?: boolean,
    subdomainId?: number,
  ) => void;
}

function NewLanderDialog({
  open,
  onClose,
  onConfirm,
  onComponentCreated,
}: NewLanderDialogProps) {
  const defaultName = `Landing Page (${friendlyDateTime(new Date())})`;
  const [name, setName] = useState(defaultName);
  const [template, setTemplate] = useState<Template | null>(null);
  const [url, setUrl] = useState("");
  const [isAdvanced, setIsAdvanced] = useState(true);
  const { templates } = useLanderTemplates();
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComponent, setProcessingComponent] =
    useState<ApiSimpleComponent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { selectedProject } = useSelectedProject();
  const { subdomains } = useSubdomains();
  const [selectedAd, setSelectedAd] = useState<AdOption | null>(null);
  const [createSmartLink, setCreateSmartLink] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Manual, 1: From Ad, 2: With AI
  const [pendingAdId, setPendingAdId] = useState<number | undefined>(undefined);
  const [pendingCreateSmartLink, setPendingCreateSmartLink] = useState<
    boolean | undefined
  >(undefined);
  const [inspirationSource, setInspirationSource] = useState<"url" | "upload">(
    "url",
  );
  const [inspirationUploading, setInspirationUploading] = useState(false);
  const [inspirationFileName, setInspirationFileName] = useState<string | null>(
    null,
  );
  const [subdomainId, setSubdomainId] = useState<number | undefined>();

  const selectableSubdomains = useMemo(
    () => getSelectableSubdomains(subdomains),
    [subdomains],
  );
  const showSubdomainSelector = selectableSubdomains.length > 1;

  const previewSubdomain = useMemo(() => {
    if (subdomainId) {
      return subdomains.find((sd) => sd.id === subdomainId);
    }
    return getLatestLiveSubdomain(subdomains);
  }, [subdomainId, subdomains]);

  // Calculate preview URL
  const previewUrl = useMemo(() => {
    return buildPreviewUrl(name, previewSubdomain);
  }, [name, previewSubdomain]);

  // Default subdomain when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }
    if (selectableSubdomains.length === 1) {
      setSubdomainId(selectableSubdomains[0].id);
      return;
    }
    setSubdomainId(getLatestLiveSubdomain(selectableSubdomains)?.id);
  }, [open, selectableSubdomains]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setName(defaultName);
      setTemplate(null);
      setUrl("");
      setPrompt("");
      setSelectedAd(null);
      setCreateSmartLink(false);
      setError(null);
      setActiveTab(0);
      setPendingAdId(undefined);
      setPendingCreateSmartLink(undefined);
      setIsAdvanced(false);
      setInspirationSource("url");
      setInspirationFileName(null);
      setSubdomainId(undefined);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (isGenerating) return;

    // Validate based on active tab
    if (activeTab === 1) {
      // Create From Meta Ad: ad and template are required
      if (!selectedAd) {
        setError("Please select an ad");
        return;
      }
      if (!template) {
        setError("Template is required when creating from an ad");
        return;
      }
    } else if (activeTab === 2) {
      // Create with AI: prompt is required
      if (!prompt || prompt.trim().length < 10) {
        setError("AI prompt is required and must be at least 10 characters");
        return;
      }
    }

    setIsGenerating(true);
    setError(null);

    try {
      let component: ApiSimpleComponent | undefined;

      // Only generate component if using AI or ad
      if (activeTab === 1 || activeTab === 2) {
        const promptText =
          activeTab === 1
            ? `Create a landing page based on the ad: ${selectedAd?.name}`
            : prompt;

        const res = await generateComponent(
          selectedProject?.id as number,
          name,
          promptText,
          ApiComponentType.LANDING,
          activeTab === 2 ? url : undefined,
          template?.id,
          activeTab === 1 ? selectedAd?.id : undefined,
          isAdvanced,
        );
        component = res.component;

        // Check if component is still processing
        if (
          component?.prompt?.status ===
          ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
        ) {
          // Add component to list immediately and create landing path
          // This ensures path exists even if user closes browser during polling
          if (onComponentCreated && component) {
            onComponentCreated(
              component,
              name,
              activeTab === 1 ? selectedAd?.id : undefined,
              activeTab === 1 ? createSmartLink : undefined,
              subdomainId,
            );
          }
          // Store component for polling
          setProcessingComponent(component);
          setPendingAdId(activeTab === 1 ? selectedAd?.id : undefined);
          setPendingCreateSmartLink(
            activeTab === 1 ? createSmartLink : undefined,
          );
          setIsProcessing(true);
          setIsGenerating(false);
          return;
        }
      }

      onConfirm(
        name,
        template?.id || undefined,
        component,
        activeTab === 1 ? selectedAd?.id : undefined,
        activeTab === 1 ? createSmartLink : undefined,
        subdomainId,
      );
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to generate landing page",
      );
      setIsGenerating(false);
    }
  }, [
    name,
    template,
    prompt,
    selectedProject?.id,
    url,
    selectedAd,
    createSmartLink,
    activeTab,
    onConfirm,
    onComponentCreated,
    isAdvanced,
    isGenerating,
    subdomainId,
  ]);

  // Poll for AI completion when processing
  usePollPendingComponents({
    projectId: selectedProject?.id,
    components: processingComponent,
    enabled: isProcessing,
    interval: 3000,
    onStatusChange: (component) => {
      setIsProcessing(false);
      onConfirm(
        name,
        template?.id || undefined,
        component,
        pendingAdId,
        pendingCreateSmartLink,
        subdomainId,
      );
      // Clear pending values
      setPendingAdId(undefined);
      setPendingCreateSmartLink(undefined);
    },
  });

  // Check if form is valid based on active tab
  const isFormValid = useMemo(() => {
    // Name is always required
    if (!name || name.trim().length < 3 || name.length > 200) {
      return false;
    }

    if (activeTab === 1) {
      // Create From Meta Ad: ad and template are required
      return !!selectedAd && !!template;
    } else if (activeTab === 2) {
      // Create with AI: prompt is required (min 10 characters)
      return !!prompt && prompt.trim().length >= 10;
    }

    // Manual create: only name is required
    return true;
  }, [name, activeTab, selectedAd, template, prompt]);

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ValidatorForm onSubmit={handleSubmit}>
        <DialogTitle>Create New Landing Page</DialogTitle>
        {isGenerating && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
            }}
          >
            <Loading />
          </Box>
        )}
        {isProcessing && (
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                AI is generating your landing page
              </Typography>
              <Typography variant="body2">
                We're working on creating your landing page in the background.
                This may take a few moments. You can close this dialog and the
                page will appear in your list when it's ready.
              </Typography>
            </Alert>
          </DialogContent>
        )}
        {!isProcessing && (
          <>
            <DialogContent sx={{ marginBottom: "1px" }}>
              <Box>
                {/* Landing Page Name - Always visible */}

                <TextValidator
                  label="Landing Page Name"
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                  name="name"
                  fullWidth
                  autoFocus
                  validators={[
                    "required",
                    "minStringLength:3",
                    "maxStringLength:200",
                  ]}
                  errorMessages={[
                    "Name is required",
                    "Name must be 3 or more characters long",
                    "Name must be less than 200 characters long",
                  ]}
                  disabled={isGenerating}
                />

                {showSubdomainSelector && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" mb={2}>
                      Subdomain (Optional)
                    </Typography>
                    <SubdomainSelector
                      subdomainId={subdomainId}
                      setSubdomainId={setSubdomainId}
                      optional
                      helperText="Choose where this page will be published. You can set this later in landing page settings."
                    />
                  </Box>
                )}

                {previewUrl && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      display="block"
                      gutterBottom
                    >
                      Preview URL:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {previewUrl}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      You can change the subdomain and path from the landing
                      page settings after creation.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogContent>
              {/* Creation Method Tabs */}
              <Box>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  sx={{ width: `calc(100% + 48px)`, marginLeft: "-24px" }}
                  variant="fullWidth"
                >
                  <Tab label="Manually Create" />
                  <Tab label="Create From Meta Ad" />
                  <Tab label="Create with AI" />
                </Tabs>

                {/* Tab Panel Content */}
                <Box sx={{ mt: 4 }}>
                  {/* Manually Create Tab */}
                  {activeTab === 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Start from Template (Optional)
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Choose an existing template to start from. Leave blank
                        to create a blank landing page.
                      </Typography>
                      <ValidatorAutocomplete
                        label="Template"
                        name="template"
                        value={template}
                        options={templates.map((t) => ({
                          id: t.id!,
                          name: t.name || "",
                        }))}
                        getOptionLabel={(option: Template) => option.name}
                        onChange={(e) => setTemplate(e.target.value)}
                        disabled={isGenerating}
                      />
                    </Box>
                  )}

                  {/* Create From Meta Ad Tab */}
                  {activeTab === 1 && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Select Ad (Required)
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Select an existing ad to create a landing page using
                          AI. The AI will analyze the ad and generate a matching
                          landing page.
                        </Typography>
                        <AdSelector
                          value={selectedAd}
                          onChange={setSelectedAd}
                          label="Select Ad"
                          disabled={isGenerating}
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Template (Required)
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          A template is required when creating from an ad. The
                          AI will use this template as a structural and styling
                          base.
                        </Typography>
                        <ValidatorAutocomplete
                          label="Template"
                          name="template-ad"
                          value={template}
                          options={templates.map((t) => ({
                            id: t.id!,
                            name: t.name || "",
                          }))}
                          getOptionLabel={(option: Template) => option.name}
                          onChange={(e) => setTemplate(e.target.value)}
                          disabled={isGenerating}
                        />
                      </Box>

                      <Box>
                        <Stack>
                          <FormControlLabel
                            componentsProps={{
                              typography: {
                                variant: "body1",
                              },
                            }}
                            control={
                              <Switch
                                checked={createSmartLink}
                                onChange={(e) =>
                                  setCreateSmartLink(e.target.checked)
                                }
                                disabled={isGenerating}
                              />
                            }
                            label="Use Smart link for this ad"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Automatically link this landing page to the selected
                            ad as a smart link. Note, this will only take effect
                            once the landing page is active.
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  )}

                  {/* Create with AI Tab */}
                  {activeTab === 2 && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          AI Prompt (Required)
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Provide a detailed prompt to have AI generate your
                          landing page. The AI will use this prompt to create a
                          customized landing page.
                        </Typography>
                        <TextValidator
                          multiline
                          rows={6}
                          fullWidth
                          label="AI Prompt"
                          value={prompt}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPrompt(e.target.value)
                          }
                          name="prompt"
                          disabled={isGenerating}
                          validators={[
                            "required",
                            "minStringLength:10",
                            "maxStringLength:1024",
                          ]}
                          errorMessages={[
                            "AI prompt is required",
                            "Prompt must be 10 or more characters long",
                            "Prompt must be less than 1024 characters long",
                          ]}
                          placeholder='e.g. Create a landing page for "Luminara Silk," a premium skin cream designed for women aged 45–60 with aging skin concerns. The page should highlight its anti-aging benefits, dermatologist-developed formula, and promise of visible results. The tone must be sophisticated, trustworthy, and empowering. The page should have a hero section with a title, a description, and a call to action button.'
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Template (Optional)
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Optionally choose a template. If selected, the AI will
                          use it as a structural and styling base.
                        </Typography>
                        <ValidatorAutocomplete
                          label="Template"
                          name="template"
                          value={template}
                          options={templates.map((t) => ({
                            id: t.id!,
                            name: t.name || "",
                          }))}
                          getOptionLabel={(option: Template) => option.name}
                          onChange={(e) => setTemplate(e.target.value)}
                          disabled={isGenerating}
                        />
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Inspiration (Optional)
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Use a page URL for design inspiration or upload an
                          image. The AI will use it as a visual reference.
                        </Typography>
                        <ToggleButtonGroup
                          value={inspirationSource}
                          exclusive
                          onChange={(_, v) =>
                            v != null && setInspirationSource(v)
                          }
                          size="small"
                          sx={{ mb: 1.5 }}
                        >
                          <ToggleButton value="url">Page URL</ToggleButton>
                          <ToggleButton value="upload">
                            Upload image
                          </ToggleButton>
                        </ToggleButtonGroup>
                        {inspirationSource === "url" && (
                          <TextValidator
                            label="Inspiration URL"
                            value={url}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setUrl(e.target.value)}
                            name="url"
                            fullWidth
                            disabled={isGenerating}
                            validators={url.length > 0 ? ["isDomain"] : []}
                            errorMessages={["Must be a valid URL"]}
                            placeholder="https://example.com"
                          />
                        )}
                        {inspirationSource === "upload" && (
                          <Box>
                            <UploaderButton
                              tmp
                              useDropZone
                              dropZoneText="Drag and drop an image here or click to browse"
                              accept="image/*"
                              project={selectedProject!}
                              path=""
                              type={ApiMediaType.IMAGE_MEDIA}
                              hidden
                              uploading={inspirationUploading}
                              text="Uploading..."
                              onProgress={(percent, status) => {
                                setInspirationUploading(
                                  percent < 100 && status !== "finished",
                                );
                              }}
                              onFinish={(result, file) => {
                                setUrl(
                                  result.readUrl ?? result.publicUrl ?? "",
                                );
                                setInspirationFileName(file.name);
                                setInspirationUploading(false);
                              }}
                              onError={() => setInspirationUploading(false)}
                            />
                            {inspirationFileName && url && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                              >
                                {inspirationFileName} uploaded. Clear the
                                inspiration field by switching to Page URL or
                                closing the dialog.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Stack>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAdvanced}
                              onChange={() => setIsAdvanced(!isAdvanced)}
                            />
                          }
                          label="Enable premium build mode"
                          value={isAdvanced}
                        />
                        <Typography variant="body2">
                          Best for complex, or more creative landing pages. Uses
                          a more powerful model and in-depth coding approach, so
                          requires more credits.
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>

              {error && (
                <Box mt={2}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              )}
            </DialogContent>
          </>
        )}

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            {isProcessing ? "Close" : "Cancel"}
          </Button>
          {!isProcessing && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isGenerating || !isFormValid}
            >
              {isGenerating ? "Generating..." : "Create"}
            </Button>
          )}
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default NewLanderDialog;
